import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import AuctionStatusComponent from '@Components/Auction/AuctionStatus';
import AuctionTypeDisplay from '@Components/Auction/AuctionTypeDisplay';
import Icon, { IconSizes } from '@Components/Icon';
import Image from '@Components/Image';
import { showConfirmationModal, showSuccessModal } from '@Components/Modal/modalHelpers';
import PriceDisplay from '@Components/PriceDisplay';
import Typography from '@Components/Typography';
import { SEARCH_ITEM_TITLE_LENGTH } from '@Config/constants';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import {
  AuctionFragment,
  GetAuctionsDocument,
  GetMyAuctionsDocument,
  useDeleteAuctionMutation,
} from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import PlaceholderImage from '@Images/placeholder.png';
import { getLastBid } from '@Utils/auction';
import { getOperationName } from '@Utils/graphql';
import { convertMoneyToCents } from '@Utils/money';
import { truncate } from '@Utils/string';
import { noop } from '@Utils/util';

import styles from './AuctionItem.module.scss';

export interface AuctionItemProps {
  auction: AuctionFragment;
  isOwner: boolean;
  isDeletable: boolean;
  onClick?: () => void;
}

const AuctionItem: FunctionComponent<AuctionItemProps> = ({
  auction: auctionItem,
  isOwner,
  isDeletable,
  onClick = noop,
}) => {
  const t = useTranslations();
  const dispatch = useDispatch();

  const lastBid = getLastBid(auctionItem);
  const lastBigToPrice = convertMoneyToCents(Number(lastBid));
  const image = auctionItem.mainImageThumbnail || PlaceholderImage;

  const [deleteAuction] = useDeleteAuctionMutation({
    variables: {
      input: {
        id: auctionItem.id,
      },
    },
    refetchQueries: [getOperationName(GetAuctionsDocument), getOperationName(GetMyAuctionsDocument)],
  });

  const handleOnItemDelete = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      showConfirmationModal({
        title: t(Messages.auctionDeleteModalTitle),
        data: {
          onConfirmButtonClick: () => {
            deleteAuction().then(() =>
              dispatch(showSuccessModal(t(Messages.btnOk), () => {}, t(Messages.auctionDeleteSuccessModalTitle))),
            );
          },
        },
      }),
    );
  };

  return (
    <Link
      onClick={onClick}
      className={styles.container}
      to={
        isOwner
          ? RouteConfig.MyAuctionPreview.buildLink({ slug: auctionItem.slug })
          : RouteConfig.AuctionPreview.buildLink({ slug: auctionItem.slug })
      }
    >
      <div className={styles.pictureContainer}>
        <Image src={image || PlaceholderImage} className={styles.picture} alt={auctionItem.title} />
      </div>
      <div className={styles.content}>
        <div className={classNames(styles.title)}>
          <Typography
            tag="span"
            color="black"
            msg={truncate(auctionItem.title, SEARCH_ITEM_TITLE_LENGTH)}
            size="m"
            bold
          />
        </div>
        <AuctionTypeDisplay type={auctionItem.auctionType} className={styles.pillStyle} />
        <Typography
          className={styles.bidStatus}
          msg={lastBid ? t(Messages.auctionBidPrice) : t(Messages.auctionStartingPrice)}
          tag="div"
          color="primary"
          bold
        />
        <div className={styles.bidingContainer}>
          <PriceDisplay
            tag="span"
            color="gray"
            priceColor="primary"
            priceSize="l"
            size="s"
            price={lastBid ? lastBigToPrice : Number(auctionItem.initialPrice)}
            pricingType={t(Messages.labelEur)}
            priceBold
          />
          <Typography
            className={styles.responsiveText}
            msg={
              auctionItem.totalBidCount
                ? t(Messages.auctionBidders, { value: auctionItem.totalBidCount })
                : t(Messages.auctionNoBiddersPlaceholder)
            }
            tag="span"
            size="s"
            color="black"
          />
        </div>
      </div>
      <AuctionStatusComponent status={auctionItem.status} expirationDate={auctionItem.expirationDate}>
        {isDeletable && (
          <Icon onClick={handleOnItemDelete} className={styles.deleteIcon} size={IconSizes.s} icon="delete" />
        )}
      </AuctionStatusComponent>
    </Link>
  );
};

export default AuctionItem;
